import type {
  SurveyDevelopmentNeed,
  SurveyPlanStatusValue,
  SurveyResponseViewQuestion,
  SurveyTarget,
} from '../../learning-development/survey/surveyTypes';

/**
 * The questionnaire a survey puts in front of the employee — its live one, or, once the survey is
 * deleted or has none, the one they answered.
 */
export interface MySurveyQuestionnaire {
  id: string;
  title: string;
  description: string | null;
  isActive: boolean;
}

/**
 * One survey as the signed-in employee sees it — `GET /api/self-service/surveys`. No respondent
 * count and nobody else's anything: how many answered is the administrator's question.
 */
export interface MySurvey {
  id: string;
  title: string;
  /** EMPTY MEANS ALL — no division was singled out. */
  orgUnits: SurveyTarget[];
  /** EMPTY MEANS ALL — no position was singled out. */
  positions: SurveyTarget[];
  /** Null (or absent off the wire) when there is nothing to answer yet. */
  questionnaire?: MySurveyQuestionnaire | null;
  /** When the caller answered `questionnaire`. Absent while they have not. */
  respondedAt?: string | null;
  /** The lifecycle's latest step — `MY_SURVEY_STAGE`, numeric on the wire. */
  stage: MySurveyStageValue;
  /** When Learning & Development assessed the response. */
  assessedAt?: string | null;
  assessedByName?: string | null;
  /** When the development plan was first written — the step and its date; the plan itself is Preview's, once approved. */
  developmentPlanAt?: string | null;
  developmentPlanByName?: string | null;
  /**
   * When the approved plan was SENT to the reader — on the single read only. Absent means it has
   * not been: approved is not sent, and the plan does not open until it is.
   */
  developmentPlanSentAt?: string | null;
  /** When the employee accepted or declined the approved plan. */
  planDecidedAt?: string | null;
  /** When Learning & Development assessed the training's impact — the life cycle's step. Absent until then. */
  impactAssessedAt?: string | null;
  /** The training started on the accepted plan — absent until the employee records one. */
  training?: MySurveyTraining | null;
  /** The plan's approval, on the single read only; absent on a list row and before submission. */
  approval?: MySurveyApproval | null;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  /** The SURVEY's own live-or-deleted state. */
  isActive: boolean;
}

/** Whether the account can answer surveys at all. `error` is absent when it can. */
export interface MySurveyAccount {
  error?: string | null;
}

/** When the plan went to its approver and who decided it. No reason — that is Learning & Development's. */
export interface MySurveyApproval {
  submittedAt: string;
  decidedAt?: string | null;
  decidedByName?: string | null;
}

/**
 * The lifecycle of an employee's response — mirrors `MySurveyStage`, numeric on the wire. The
 * FACET values are the enum NAMES (strings), the product's facet exception.
 */
export const MY_SURVEY_STAGE = {
  NotAnswered: 0,
  Answered: 1,
  Assessed: 2,
  DevelopmentPlan: 3,
  Submitted: 4,
  Approved: 5,
  Rejected: 6,
  /** The employee accepted the approved plan — final. */
  Accepted: 7,
  /** The employee declined it, with a reason; Learning & Development revises it. */
  Declined: 8,
} as const;

export type MySurveyStageValue = (typeof MY_SURVEY_STAGE)[keyof typeof MY_SURVEY_STAGE];

/** The server's own words (`MySurveyService.StageLabel`) — one vocabulary on the row, the tab and the pill. */
export const MY_SURVEY_STAGE_LABELS: Record<MySurveyStageValue, string> = {
  0: 'Not answered',
  1: 'Answered',
  2: 'Assessed',
  3: 'Development plan',
  4: 'For approval',
  5: 'Approved',
  6: 'Rejected',
  7: 'Accepted',
  8: 'Declined',
};

/** The caller's own answers, in words — `GET …/my-answers`. No assessment, no plan. */
export interface MySurveyAnswers {
  questionnaireTitle: string;
  questionnaireDescription?: string | null;
  respondedAt: string;
  remarks?: string | null;
  /** The caller's own display name, for a Name question. */
  myName?: string | null;
  questions: SurveyResponseViewQuestion[];
}

/**
 * The employee's development plan once APPROVED, with their own answer to it — `GET …/development-plan`,
 * what "View development plan" opens. 404 for a draft, a plan with its approver and a rejected one.
 */
export interface MySurveyDevelopmentPlan {
  /** SANITISED markup — rendered only through `<RichTextDocument>`. */
  plan?: string | null;
  needs: SurveyDevelopmentNeed[];
  /** Approved (awaiting the employee's answer), Accepted or Declined — `SURVEY_PLAN_STATUS`. */
  status: SurveyPlanStatusValue;
  /** When they accepted or declined it. Absent while they have not. */
  decidedAt?: string | null;
  /** Their reason, when they declined. */
  declineReason?: string | null;
  /** When Learning & Development assessed the training's impact. Absent until then; the levels ride `needs`. */
  impactAssessedAt?: string | null;
  /** What they concluded. */
  impactAssessmentRemarks?: string | null;
}

/** The training the employee started on their accepted plan. One per plan. */
export interface MySurveyTraining {
  title: string;
  /** Calendar dates, `YYYY-MM-DD` — never instants; format with `formatDateOnly`. */
  startDate: string;
  endDate: string;
  /** When it was first recorded — an instant. */
  recordedAt: string;
  /** Why it was last corrected. Absent while it has never been edited. */
  remarks?: string | null;
  /** When the employee marked it completed — an instant. Absent until then. */
  completedAt?: string | null;
  completionRemarks?: string | null;
  /** How it ended — `TRAINING_RESULT`. Absent while it has not, and on a training ended before the result was asked (a completion). */
  result?: TrainingResultValue | null;
  /** Whether it can be completed NOW — its end date has come, by the SERVER's calendar. Single read only. */
  canComplete?: boolean;
  /** When the report on the completed training was first submitted — an instant. Absent until then. */
  reportAt?: string | null;
  /** The report as SANITISED markup — on the single read only; rendered only through `<RichTextDocument>`. */
  report?: string | null;
  /** The files attached to the report, in order — on the single read only. Absent or empty while it carries none. */
  reportAttachments?: TrainingReportAttachment[];
}

/** One file attached to a training report: its id, to fetch or remove it, and its own name. */
export interface TrainingReportAttachment {
  id: string;
  fileName: string;
  /** Whether it was attached as a certificate — the report form's own field for them. */
  isCertificate?: boolean;
}

/** The most files one report may carry — the server's own cap, which it enforces. */
export const TRAINING_REPORT_MAX_ATTACHMENTS = 10;

/** Submitting — or revising — the report on a completed training. The report is required. */
export interface SaveMyTrainingReportRequest {
  report: string;
}

/**
 * Starting — or correcting — the training. The title and dates are required; `remarks` is required
 * on a CORRECTION (the server knows which it is) and ignored on the first recording.
 */
export interface SaveMyTrainingRequest {
  title: string;
  startDate: string;
  endDate: string;
  remarks?: string | null;
}

/** Completing the training once its end date has come. The remarks are required. */
export interface CompleteMyTrainingRequest {
  remarks: string;
}

/**
 * One training recommendation SENT to the employee, with their own track on it — their answer,
 * the training they take, and their report — `GET …/training-recommendations`. Drafts, ones with
 * their approver, rejected ones and approved ones not yet sent never arrive.
 */
export interface MySurveyTrainingRecommendation {
  id: string;
  title: string;
  /** SANITISED markup — rendered only through `<RichTextDocument>`. */
  details?: string | null;
  /** When it was sent to the employee — what made it theirs to read. */
  sentAt: string;
  /** Their OWN answer to it — `TRAINING_DECISION`, numeric on the wire. */
  decision: TrainingDecisionValue;
  decidedAt?: string | null;
  /** Their reason, when they declined. */
  declineReason?: string | null;
  /** The recommendation's own provenance — the standard header's "Created by" line. */
  createdAt?: string | null;
  createdByName?: string | null;
  /** Their enrollment — the school and its location. Absent until they enroll. */
  enrollment?: MySurveyEnrollment | null;
  /** The training they recorded on it, with its report — absent until they start one. Its title is the recommendation's. */
  training?: MySurveyTraining | null;
}

/** The backend's `SurveyTrainingResult` — how a training ended. Numeric on the wire. */
export const TRAINING_RESULT = { Completed: 0, Cancelled: 1 } as const;
export type TrainingResultValue = (typeof TRAINING_RESULT)[keyof typeof TRAINING_RESULT];
export const TRAINING_RESULT_LABELS: Record<TrainingResultValue, string> = { 0: 'Completed', 1: 'Cancelled' };

/** Ending the training taken on a recommendation — the result, and the reason (required for a cancellation only). */
export interface EndMyRecommendationTrainingRequest {
  result: TrainingResultValue;
  reason?: string | null;
}

/** The backend's `SurveyTrainingDecision` — the employee's answer to a sent recommendation. */
export const TRAINING_DECISION = { Pending: 0, Accepted: 1, Declined: 2 } as const;
export type TrainingDecisionValue = (typeof TRAINING_DECISION)[keyof typeof TRAINING_DECISION];

/**
 * Enrolling in an accepted recommendation's training — or correcting the enrollment. The school is
 * required; the location is optional, and carries no postal code.
 */
export interface SaveMyRecommendationEnrollmentRequest {
  school: string;
  province?: string | null;
  cityMunicipality?: string | null;
  barangay?: string | null;
  streetAddress?: string | null;
  /** The training duration — calendar dates, `YYYY-MM-DD`, both required. */
  startDate: string;
  endDate: string;
}

/** The employee's enrollment on a recommendation — write the location with `addressPathLabel`. */
export interface MySurveyEnrollment {
  school: string;
  province?: string | null;
  cityMunicipality?: string | null;
  barangay?: string | null;
  streetAddress?: string | null;
  /** The training duration given at enrollment — calendar dates; `formatDateOnly`. Absent on an earlier enrollment. */
  startDate?: string | null;
  endDate?: string | null;
  /** When they first enrolled — an instant. Absent on a training started before the step existed. */
  enrolledAt?: string | null;
}

/** The dates of the training taken on a recommendation; `remarks` is required on a correction. Enrolled first. */
export interface SaveMyRecommendationTrainingRequest {
  startDate: string;
  endDate: string;
  remarks?: string | null;
}

/**
 * One row of Preview › Training Recommendations — a recommendation sent to the employee, and where
 * they stand on it. The table's own record: the profile reads the whole `MySurveyTrainingRecommendation`.
 */
export interface MySurveyTrainingRecommendationRow {
  id: string;
  title: string;
  sentAt: string;
  decision: TrainingDecisionValue;
  decidedAt?: string | null;
  /** When they enrolled — an instant. Absent until they do. */
  enrolledAt?: string | null;
  /** Calendar dates, `YYYY-MM-DD` — `formatDateOnly`, never the instant path. */
  trainingStartDate?: string | null;
  trainingEndDate?: string | null;
  trainingCompletedAt?: string | null;
  /** How it ended — `TRAINING_RESULT`. Absent while it has not, and on an earlier completion. */
  trainingResult?: TrainingResultValue | null;
  reportAt?: string | null;
}

/**
 * Where the employee stands on a recommendation — the LATEST step reached, every arm a fact the
 * record already carries. Learning & Development's Personnel table reads the same steps in the
 * same words (`SurveyTrainingRecommendationPersonnelPanel`), so the two never disagree.
 * `!= null` throughout — off the wire an absent value is undefined.
 */
export function myTrainingStatus(track: {
  decision: TrainingDecisionValue;
  enrolledAt?: string | null;
  trainingStartDate?: string | null;
  trainingCompletedAt?: string | null;
  trainingResult?: TrainingResultValue | null;
}): string {
  // "Pending", the office's own word (product call, 13 Oct 2026) — never a sentence in a pill.
  if (track.decision === TRAINING_DECISION.Pending) return 'Pending';
  if (track.decision === TRAINING_DECISION.Declined) return 'Declined';
  // Ended either way — the result says which. An end with no result predates the question: a completion.
  if (track.trainingCompletedAt != null) return track.trainingResult === TRAINING_RESULT.Cancelled ? 'Training cancelled' : 'Training completed';
  if (track.trainingStartDate != null) return 'Training started';
  if (track.enrolledAt != null) return 'Enrolled';

  return 'Accepted';
}
